import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToken } from '../../../contexts/TokenContext';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { 
  Coins, 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  CreditCard, 
  Clock, 
  Zap,
  AlertCircle,
  CheckCircle,
  Settings as SettingsIcon
} from 'lucide-react';

interface TokenTransaction {
  _id: string;
  type: 'purchase' | 'usage' | 'refund' | 'adjustment' | 'auto_reload';
  amount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
  metadata?: {
    feature?: string;
    stripePaymentIntentId?: string;
  };
}

interface TokenPack {
  _id: string;
  name: string;
  description: string;
  tokenAmount: number;
  priceInCents: number;
  isPopular?: boolean;
  isActive: boolean;
}

export const TokenManagement: React.FC = () => {
  const { user } = useAuth();
  const { 
    balance, 
    freeMonthlyAllowance, 
    freeTokensRemaining, 
    totalPurchased, 
    totalUsed,
    autoReload,
    openPurchaseModal,
    refreshBalance 
  } = useToken();
  const { t } = useTranslation();
  
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [packs, setPacks] = useState<TokenPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isAccountOwner = user?.accountRole === 'Account Owner';
  // balance is the current available tokens
  const totalBalance = balance;
  // freeTokensInBalance is how many of the current balance are from free allowance
  const freeTokensInBalance = Math.min(balance, freeMonthlyAllowance);
  // purchasedTokensInBalance is the remaining purchased tokens in current balance
  const purchasedTokensInBalance = Math.max(0, balance - freeMonthlyAllowance);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch transactions
      const txResponse = await fetch(`${apiUrl}/api/tokens/transactions?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const txData = await txResponse.json();
      if (txData.success) {
        setTransactions(txData.data.transactions || []);
      }

      // Fetch available packs
      const packsResponse = await fetch(`${apiUrl}/api/tokens/packs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const packsData = await packsResponse.json();
      if (packsData.success) {
        setPacks(packsData.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch token data:', err);
      setError('Failed to load token data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshBalance();
    await fetchData();
    setRefreshing(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'purchase':
      case 'auto_reload':
        return <TrendingUp className="w-4 h-4 text-success-600 dark:text-success-400" />;
      case 'usage':
        return <TrendingDown className="w-4 h-4 text-error-600 dark:text-error-400" />;
      case 'refund':
        return <RefreshCw className="w-4 h-4 text-info-600 dark:text-info-400" />;
      default:
        return <Coins className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'purchase':
      case 'auto_reload':
        return 'text-success-600 dark:text-success-400';
      case 'usage':
        return 'text-error-600 dark:text-error-400';
      case 'refund':
        return 'text-info-600 dark:text-info-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600 dark:text-gray-400">Loading token data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Token Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage your token balance and purchase history
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Balance Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Balance</h3>
            <Coins className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalBalance.toLocaleString()}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Available tokens</p>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Free (Monthly)</h3>
            <Zap className="w-5 h-5 text-info-600 dark:text-info-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{freeTokensInBalance.toLocaleString()}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            of {freeMonthlyAllowance.toLocaleString()} allowance
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Purchased</h3>
            <CreditCard className="w-5 h-5 text-success-600 dark:text-success-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{purchasedTokensInBalance.toLocaleString()}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Paid tokens remaining</p>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Used</h3>
            <TrendingDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalUsed.toLocaleString()}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">All time usage</p>
        </div>
      </div>

      {/* Auto-Reload Status */}
      {autoReload?.enabled && (
        <div className="bg-info-50 dark:bg-blue-950/50 border border-info-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-info-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-info-900 dark:text-white mb-1">
                Auto-Reload Enabled
              </h3>
              <p className="text-sm text-info-800 dark:text-gray-200">
                Your account will automatically purchase more tokens when your balance drops below{' '}
                <span className="font-medium">{autoReload.thresholdTokens.toLocaleString()} tokens</span>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Low Balance Warning */}
      {totalBalance < 10000 && totalBalance > 0 && (
        <div className="bg-warning-50 dark:bg-yellow-950/50 border border-warning-200 dark:border-yellow-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-warning-900 dark:text-white mb-1">
                Low Token Balance
              </h3>
              <p className="text-sm text-warning-800 dark:text-gray-200 mb-3">
                Your token balance is running low. Purchase more tokens to continue using AI features.
              </p>
              {isAccountOwner && (
                <button
                  onClick={openPurchaseModal}
                  className="bg-warning-600 hover:bg-warning-700 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Buy Tokens Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Out of Tokens */}
      {totalBalance === 0 && (
        <div className="bg-error-50 dark:bg-red-950/50 border border-error-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-error-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-error-900 dark:text-white mb-1">
                Out of Tokens
              </h3>
              <p className="text-sm text-error-800 dark:text-gray-200 mb-3">
                You've used all your tokens. Purchase more to continue using AI features.
              </p>
              {isAccountOwner ? (
                <button
                  onClick={openPurchaseModal}
                  className="bg-error-600 hover:bg-error-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Buy Tokens Now
                </button>
              ) : (
                <p className="text-sm text-error-800 dark:text-gray-200">
                  Contact your account owner to purchase more tokens.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Token Packs */}
      {isAccountOwner && packs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Available Token Packs</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Purchase tokens to continue using AI features
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {packs.map((pack) => {
                const priceInDollars = pack.priceInCents / 100;
                const tokensFormatted = pack.tokenAmount >= 1000000 
                  ? `${(pack.tokenAmount / 1000000).toFixed(1)}M` 
                  : pack.tokenAmount >= 1000 
                  ? `${(pack.tokenAmount / 1000).toFixed(0)}K` 
                  : pack.tokenAmount.toString();

                return (
                  <div
                    key={pack._id}
                    className={`relative border-2 rounded-lg p-5 transition-all hover:shadow-lg ${
                      pack.isPopular
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                    }`}
                  >
                    {pack.isPopular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
                        {pack.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{pack.description}</p>
                    </div>
                    <div className="text-center mb-4">
                      <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                        ${priceInDollars.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {tokensFormatted} tokens
                      </div>
                    </div>
                    <button
                      onClick={openPurchaseModal}
                      className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                        pack.isPopular
                          ? 'bg-primary-600 hover:bg-primary-700 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Purchase
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Transactions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Balance After
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No transactions yet
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(tx.type)}
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                          {tx.type.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{tx.description}</div>
                      {tx.metadata?.feature && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Feature: {tx.metadata.feature}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-medium ${getTransactionColor(tx.type)}`}>
                        {tx.type === 'usage' ? '-' : '+'}{Math.abs(tx.amount).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                      {tx.balanceAfter.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(tx.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">About Tokens</h3>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
          <li>Tokens are used for AI-powered features like chat, document analysis, and more</li>
          <li>Free monthly allowance resets at the start of each billing cycle</li>
          <li>Purchased tokens never expire and roll over month to month</li>
          <li>Usage is tracked in real-time across all AI features</li>
          {isAccountOwner && <li>Only account owners can purchase additional tokens</li>}
        </ul>
      </div>
    </div>
  );
};
